"use client";

import { RichText } from "@payloadcms/richtext-lexical/react";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useComparingProductsStore } from "@/features/products/ui/store";
import { useSiteSettings } from "@/features/settings/ui/hooks/useSiteSettings";
import { Link, useRouter } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { Image } from "@/lib/client/components/Image";
import { NotFound } from "@/lib/client/components/NotFound";
import { useCountryStore, useShoppingCartStore } from "@/lib/client/stores";
import { placeholderPaths } from "@/lib/shared/constants";
import type { ProductVariantForClient } from "@/lib/shared/types/interfaces";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import {
  AspectRatio,
  BackNav,
  Box,
  Button,
  Container,
  HStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  VStack,
} from "@/ui";

function getStockLabel(
  stock: number | null | undefined,
  generalT: ReturnType<typeof useTranslations>,
) {
  if (stock === 0) return generalT("out_of_stock");
  if ((stock ?? 0) >= 10) return generalT("in_stock");
  return `${generalT("only")} ${stock ?? 0} ${generalT("left")}`;
}

export const CompareContainer = () => {
  const router = useRouter();
  const generalT = useTranslations("general");
  const productDetailsT = useTranslations("product_details");
  const notFoundT = useTranslations("not_found");
  const { country } = useCountryStore();
  const { imageRatio } = useSiteSettings();
  const { addItem, setIsOpen } = useShoppingCartStore();
  const { products, removeById, clear } = useComparingProductsStore();

  const initialVariants = useMemo(
    () =>
      products.reduce<Record<string, ProductVariantForClient>>((acc, product) => {
        if (product.variants[0]) {
          acc[String(product.id)] = product.variants[0];
        }
        return acc;
      }, {}),
    [products],
  );
  const [selectedVariants, setSelectedVariants] = useState(initialVariants);
  const labelColumnWidth = 140;
  const productColumnWidth = useMemo(() => {
    if (products.length <= 1) return 320;
    if (products.length === 2) return 300;
    if (products.length === 3) return 270;
    return 240;
  }, [products.length]);
  const tableWidth = labelColumnWidth + products.length * productColumnWidth;

  // Track compare page view
  useEffect(() => {
    if (products.length > 0) {
      trackEvent(AnalyticsEvents.COMPARE_PAGE_VIEWED, {
        product_count: products.length,
        product_ids: products.map((p) => p.id),
      });
    }
  }, [products.length]);

  const handleClearCompare = () => {
    trackEvent(AnalyticsEvents.COMPARE_CLEARED, {
      product_count: products.length,
    });
    clear();
  };

  const handleRemoveProduct = (productId: string) => {
    trackEvent(AnalyticsEvents.PRODUCT_COMPARE_REMOVED, {
      product_id: productId,
      compare_count: products.length - 1,
    });
    removeById(productId);
  };

  const handleVariantChange = (productId: string, variant: ProductVariantForClient) => {
    trackEvent(AnalyticsEvents.COMPARE_VARIANT_CHANGED, {
      product_id: productId,
      variant_id: variant.variant.id,
    });
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: variant,
    }));
  };

  const handleAddToCart = (product: (typeof products)[0], variant: ProductVariantForClient) => {
    trackEvent(AnalyticsEvents.COMPARE_ADD_TO_CART, {
      product_id: product.id,
      sku: product.sku,
      variant_id: variant.variant.id,
    });
    addItem(product, variant.variant);
    setIsOpen(true);
  };

  if (products.length === 0) {
    return (
      <Container>
        <BackNav onBack={() => router.back()} />
        <NotFound description={notFoundT("compare")} />
      </Container>
    );
  }

  return (
    <Container>
      <BackNav onBack={() => router.back()} />
      <VStack className="gap-4">
        <HStack className="w-full items-center justify-between">
          <Text className="text-xl font-semibold">{generalT("compared_products")}</Text>
          <Button
            variant="outline"
            onClick={handleClearCompare}
          >
            {generalT("clear_compare")}
          </Button>
        </HStack>
        <Table
          className="mx-auto table-fixed"
          style={{ width: `${tableWidth}px` }}
        >
          <colgroup>
            <col style={{ width: `${labelColumnWidth}px` }} />
            {products.map((product) => (
              <col
                key={`${product.id}-col`}
                style={{ width: `${productColumnWidth}px` }}
              />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>{generalT("name")}</TableHead>
              {products.map((product) => (
                <TableHead
                  key={product.id}
                  className="h-[340px] align-top"
                >
                  <VStack className="h-full gap-2">
                    <AspectRatio
                      ratio={imageRatio}
                      className="relative"
                    >
                      <Image
                        src={
                          product.images?.[0]?.thumbnailSrc ||
                          product.images?.[0]?.src ||
                          placeholderPaths.image
                        }
                        alt={product.images?.[0]?.alt || product.name}
                        fill
                        className="rounded-md object-cover"
                        sizes="(max-width: 768px) 90vw, 250px"
                      />
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute right-2 top-2 z-10 h-7 w-7"
                        onClick={() => handleRemoveProduct(String(product.id))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </AspectRatio>
                    <Link
                      href={`/products/${product.sku}`}
                      className="hover:underline"
                    >
                      <Text className="font-product-title min-h-10 line-clamp-2 text-sm font-medium">
                        {product.name}
                      </Text>
                    </Link>
                  </VStack>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>{productDetailsT("details")}</TableCell>
              {products.map((product) => (
                <TableCell key={`${product.id}-description`}>
                  {product.description ? (
                    <Box className="max-h-56 min-h-40 overflow-y-auto">
                      <RichText
                        className="prose prose-sm max-w-none whitespace-pre-wrap break-words"
                        data={product.description}
                      />
                    </Box>
                  ) : (
                    <Text className="text-sm text-muted-foreground">-</Text>
                  )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>{generalT("variants")}</TableCell>
              {products.map((product) => {
                const selectedVariant = selectedVariants[String(product.id)] ?? product.variants[0];
                return (
                  <TableCell key={`${product.id}-variant`}>
                    <Select
                      value={selectedVariant?.variant.id}
                      onValueChange={(variantId) => {
                        const nextVariant = product.variants.find(
                          (variant) => variant.variant.id === variantId,
                        );
                        if (!nextVariant) return;
                        handleVariantChange(String(product.id), nextVariant);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={generalT("variants")} />
                      </SelectTrigger>
                      <SelectContent>
                        {product.variants.map((variant) => (
                          <SelectItem
                            key={variant.variant.id}
                            value={variant.variant.id}
                          >
                            {translateProductVariantLabel(productDetailsT, variant.variant.variant)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell>{generalT("price")}</TableCell>
              {products.map((product) => {
                const selectedVariant = selectedVariants[String(product.id)] ?? product.variants[0];
                if (!selectedVariant) return <TableCell key={`${product.id}-price`} />;

                return (
                  <TableCell key={`${product.id}-price`}>
                    {selectedVariant.onSalePrice ? (
                      <HStack className="items-center gap-2">
                        <Text className="font-price line-through text-muted-foreground">
                          {country.symbol}
                          {Number(selectedVariant.price * country.exchangeRate).toFixed(2)}
                        </Text>
                        <Text className="font-price text-red-500">
                          {country.symbol}
                          {Number(selectedVariant.onSalePrice * country.exchangeRate).toFixed(2)}
                        </Text>
                      </HStack>
                    ) : (
                      <Text className="font-price">
                        {country.symbol}
                        {Number(selectedVariant.price * country.exchangeRate).toFixed(2)}
                      </Text>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell>{generalT("stock")}</TableCell>
              {products.map((product) => {
                const selectedVariant = selectedVariants[String(product.id)] ?? product.variants[0];
                return (
                  <TableCell key={`${product.id}-stock`}>
                    {getStockLabel(selectedVariant?.stock, generalT)}
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell>{generalT("add_to_cart")}</TableCell>
              {products.map((product) => {
                const selectedVariant = selectedVariants[String(product.id)] ?? product.variants[0];
                return (
                  <TableCell key={`${product.id}-action`}>
                    <Box className="w-full">
                      <Button
                        className="w-full"
                        disabled={!selectedVariant || selectedVariant.stock === 0}
                        onClick={() => {
                          if (!selectedVariant) return;
                          handleAddToCart(product, selectedVariant);
                        }}
                      >
                        {generalT("add_to_cart")}
                      </Button>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </VStack>
    </Container>
  );
};
